<template>
  <div class="employment-action-column">
    <WdsMenu z-index="1000" placement="bottom-end">
      <template #trigger>
        <WdsButtonGhost :label="$t('More options')" data-test="employment-actions-menu">
          <WdsIcon name="overflow-menu-horizontal" />
        </WdsButtonGhost>
      </template>

      <WdsMenuOption data-test="see-worker-details-option" @click="seeWorkerDetails">
        <WdsStack direction-row align-center gap-sm>
          <WdsIcon name="person" :size="16" />
          {{ $t('See worker details') }}
        </WdsStack>
      </WdsMenuOption>

      <WdsMenuDivider />

      <WdsMenuOption
        theme="SUCCESS"
        data-test="update-employment-button"
        :disabled="loading"
        @click="update"
      >
        <WdsStack direction-row align-center gap-sm>
          <WdsIcon name="checkmark-outline" :size="16" />
          {{ $t('Confirm update') }}
        </WdsStack>
      </WdsMenuOption>
    </WdsMenu>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import {
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuDivider,
  WdsMenuOption,
  WdsStack,
} from '@worksome/wds-components'
import { useMutation } from '@vue/apollo-composable'
import swal from 'sweetalert'
import { FragmentType, graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useTablePanels } from '@/components/GraphqlDataTable/useTablePanels'

const { openPartnerWorkerDetailPanel } = useTablePanels()

const EmploymentActionColumnUpdated = graphql(`
  fragment EmploymentActionColumnUpdated on Employment {
    id
    worker {
      id
    }
  }
`)

const APPROVE_EMPLOYMENT_CHANGES = graphql(`
  mutation ApproveEmploymentChanges($input: ApproveEmploymentChangesInput!) {
    approveEmploymentChanges(input: $input) {
      id
      isOnboarded
    }
  }
`)

const props = defineProps<{
  row: FragmentType<typeof EmploymentActionColumnUpdated>
}>()

const emit = defineEmits<{
  'employment-updates-approved': []
}>()
const column = computed(() => useFragment(EmploymentActionColumnUpdated, props.row))

const {
  mutate: approveEmploymentChanges,
  loading,
  error,
} = useMutation(APPROVE_EMPLOYMENT_CHANGES, {
  clientId: 'publicApi',
})

const seeWorkerDetails = () => {
  openPartnerWorkerDetailPanel({
    workerId: column.value.worker.id,
    employmentId: column.value.id,
  })
}

const update = async () => {
  await approveEmploymentChanges({
    input: {
      employment: column.value.id,
    },
  })

  if (error.value) {
    swal({
      title: $t('Error'),
      text: $t('Could not approve employment changes.'),
      type: 'error',
      showConfirmButton: false,
      allowOutsideClick: true,
    })
  } else {
    emit('employment-updates-approved')
  }
}
</script>

<style scoped lang="scss">
.employment-action-column {
  :deep(.wds-menu) {
    width: max-content;
  }
}
</style>
